import { useEffect, useState } from "react";
import { Check, Circle, Loader2, Square, X } from "lucide-react";

const VISIBLE_STEPS = 12;

export function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

// «Trabajando… 23 s» + una línea por paso; el comando va plegado en una línea y al pulsar el paso se ve completo.
export function ProgressSteps({ steps = [], startedAt, plan = [], onCancel }) {
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState({});
  const [stopping, setStopping] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const seconds = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  const shown = steps.slice(-VISIBLE_STEPS);
  const hidden = steps.length - shown.length;

  return (
    <div data-testid="progress-steps" className="text-sm text-muted-foreground space-y-1.5">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Trabajando…</span>
        <span data-testid="progress-elapsed" className="tabular-nums">{formatElapsed(seconds)}</span>
        {onCancel && (
          <button
            type="button"
            data-testid="progress-cancel"
            disabled={stopping}
            onClick={() => {
              setStopping(true); // respuesta inmediata: el panel tarda unos segundos en cortar y antes no se veía nada
              onCancel();
            }}
            className="ml-auto flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-60"
          >
            <Square className="h-3 w-3" /> {stopping ? "Deteniendo…" : "Detener"}
          </button>
        )}
      </div>
      {stopping && (
        <p data-testid="progress-stopping" className="text-xs text-amber-500">
          Deteniendo… el Orquestador corta en cuanto termina lo que está haciendo (unos segundos). Lo que ya se aplicó queda con su respaldo.
        </p>
      )}
      {plan.length > 0 && (
        <div data-testid="progress-plan" className="rounded-lg border border-border px-3 py-2 space-y-1">
          <p className="text-xs font-medium text-foreground">
            Plan · {plan.filter((p) => p.estado === "hecho").length} de {plan.length}
          </p>
          <ul className="space-y-0.5">
            {plan.map((p, i) => (
              <li key={i} data-testid={`progress-plan-${i}`} data-state={p.estado} className="flex items-start gap-2 text-xs">
                {p.estado === "hecho" ? (
                  <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                ) : p.estado === "en_curso" ? (
                  <Loader2 className="h-3.5 w-3.5 mt-0.5 animate-spin shrink-0" />
                ) : p.estado === "bloqueado" ? (
                  <X className="h-3.5 w-3.5 mt-0.5 text-red-500 shrink-0" />
                ) : (
                  <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-50" />
                )}
                <span className={p.estado === "hecho" ? "line-through opacity-70" : p.estado === "en_curso" ? "text-foreground" : ""}>{p.texto}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hidden > 0 && <p className="pl-6 text-xs">+ {hidden} paso(s) anteriores</p>}
      <ul className="space-y-1">
        {shown.map((step, i) => {
          const isLast = i === shown.length - 1;
          const expanded = !!open[step.id];
          const toggle = () => setOpen((cur) => ({ ...cur, [step.id]: !cur[step.id] }));
          return (
            <li key={step.id} data-testid={`progress-step-${i}`} className="pl-1">
              <div className="flex items-start gap-2">
                {isLast ? <Loader2 className="h-3.5 w-3.5 mt-0.5 animate-spin shrink-0" /> : <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <span className={isLast ? "text-foreground" : ""}>{step.label}</span>
                  {step.detail && (
                    <button
                      type="button"
                      data-testid={`progress-step-detail-${i}`}
                      onClick={toggle}
                      title={expanded ? "Plegar" : "Ver completo"}
                      className={`mt-0.5 block w-full text-left rounded bg-muted/40 px-2 py-1 font-mono text-xs ${expanded ? "whitespace-pre-wrap break-all" : "truncate"}`}
                    >
                      {step.detail}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
